import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import MainLayout from '@/layouts/MainLayout.vue'
import SimpleLayout from '@/layouts/SimpleLayout.vue'
import '@/types/router' // 导入路由类型定义

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: SimpleLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: () => import('@/views/login.vue'),
        meta: { title: '登录' },
      },
    ],
  },
  {
    path: '/',
    component: MainLayout,
    redirect: '/dashboard',
    children: [
      // 仪表盘
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard.vue'),
        meta: { title: '仪表盘', icon: 'HomeFilled', keepAlive: true, closable: false },
      },
      // 用户管理
      {
        path: 'user',
        redirect: '/user/management',
        meta: { title: '用户管理', icon: 'UserFilled' },
        children: [
          {
            path: 'management',
            name: 'UserManagement',
            component: () => import('@/views/user-management.vue'),
            meta: { title: '用户管理', keepAlive: true },
          },
          {
            path: 'detail/:id',
            name: 'UserDetail',
            component: () => import('@/views/user-detail.vue'),
            meta: { title: '用户详情', hidden: true, keepAlive: true },
          },
          {
            path: 'relations',
            name: 'UserRelationList',
            component: () => import('@/views/user-relation-management.vue'),
            meta: { title: '用户关系管理', keepAlive: true },
          },
        ],
      },

      // 群组管理
      {
        path: 'group',
        redirect: '/group/management',
        meta: { title: '群组管理', icon: 'MessageBox' },
        children: [
          {
            path: 'detail/:id',
            name: 'GroupDetail',
            component: () => import('@/views/group-detail.vue'),
            meta: { title: '群组详情', hidden: true, keepAlive: true },
          },
          {
            path: 'camp/management',
            name: 'CampManagement',
            component: () => import('@/views/camp-management.vue'),
            meta: { title: '大本营管理', keepAlive: true },
          },
          {
            path: 'camp/detail/:id',
            name: 'CampDetail',
            component: () => import('@/views/camp-detail.vue'),
            meta: { title: '大本营详情', hidden: true, keepAlive: true },
          },
          {
            path: 'battle/management',
            name: 'BattleManagement',
            component: () => import('@/views/battle-management.vue'),
            meta: { title: '对线群管理', keepAlive: true },
          },
          {
            path: 'battle/detail/:id',
            name: 'BattleDetail',
            component: () => import('@/views/battle-detail.vue'),
            meta: { title: '对线群详情', hidden: true, keepAlive: true },
          },
        ],
      },

      // 聊天管理
      {
        path: 'chat',
        redirect: '/chat/channel/management',
        meta: { title: '聊天管理', icon: 'ChatDotRound' },
        children: [
          {
            path: 'channel/management',
            name: 'ChatChannelManagement',
            component: () => import('@/views/chat-channel-management.vue'),
            meta: { title: '聊天频道', keepAlive: true },
          },
          {
            path: 'channel/detail/:id',
            name: 'ChatChannelDetail',
            component: () => import('@/views/chat-channel-detail.vue'),
            meta: { title: '频道详情', hidden: true, keepAlive: true },
          },
          {
            path: 'member/management',
            name: 'ChatChannelMemberManagement',
            component: () => import('@/views/chat-channel-member-management.vue'),
            meta: { title: '频道成员', keepAlive: true },
          },
          {
            path: 'member/detail/:id',
            name: 'ChatChannelMemberDetail',
            component: () => import('@/views/chat-channel-member-detail.vue'),
            meta: { title: '成员详情', hidden: true, keepAlive: true },
          },
          {
            path: 'message/management',
            name: 'ChatMessageManagement',
            component: () => import('@/views/chat-channel-message-management.vue'),
            meta: { title: '聊天消息', keepAlive: true },
          },
          {
            path: 'message/detail/:id',
            name: 'ChatMessageDetail',
            component: () => import('@/views/chat-channel-message-detail.vue'),
            meta: { title: '消息详情', hidden: true, keepAlive: true },
          },
        ],
      },

      // 内容管理
      {
        path: 'content',
        redirect: '/content/post/management',
        meta: { title: '内容管理', icon: 'DataAnalysis' },
        children: [
          {
            path: 'post/management',
            name: 'PostManagement',
            component: () => import('@/views/post-management.vue'),
            meta: { title: '帖子管理', keepAlive: true },
          },
          {
            path: 'post/detail/:id',
            name: 'PostDetail',
            component: () => import('@/views/post-detail.vue'),
            meta: { title: '帖子详情', hidden: true, keepAlive: true },
          },
          {
            path: 'post/:id/comments',
            name: 'PostComments',
            component: () => import('@/views/post-comments.vue'),
            meta: { title: '帖子评论管理', hidden: true, keepAlive: true },
          },
          {
            path: 'comment/management',
            name: 'CommentManagement',
            component: () => import('@/views/comment-management.vue'),
            meta: { title: '评论管理', keepAlive: true },
          },
          {
            path: 'comment/detail/:id',
            name: 'CommentDetail',
            component: () => import('@/views/comment-detail.vue'),
            meta: { title: '评论详情', hidden: true, keepAlive: true },
          },
          {
            path: 'report/management',
            name: 'ReportList',
            component: () => import('@/views/report-management.vue'),
            meta: { title: '举报审核', keepAlive: true },
          },
          {
            path: 'sticker/management',
            name: 'StickerList',
            component: () => import('@/views/sticker-management.vue'),
            meta: { title: '表情包管理', keepAlive: true },
          },
        ],
      },

      // 文件管理
      {
        path: 'file',
        redirect: '/file/management',
        meta: { title: '文件管理', icon: 'FolderOpened' },
        children: [
          {
            path: 'management',
            name: 'FileManagement',
            component: () => import('@/views/file-management.vue'),
            meta: { title: '文件管理', keepAlive: true },
          },
          {
            path: 'detail/:id',
            name: 'FileDetail',
            component: () => import('@/views/file-detail.vue'),
            meta: { title: '文件详情', hidden: true, keepAlive: true },
          },
        ],
      },

      // 设备管理
      {
        path: 'device',
        redirect: '/device/management',
        meta: { title: '设备管理', icon: 'Iphone' },
        children: [
          {
            path: 'management',
            name: 'DeviceManagement',
            component: () => import('@/views/device-management.vue'),
            meta: { title: '设备管理', keepAlive: true },
          },
          {
            path: 'detail/:id',
            name: 'DeviceDetail',
            component: () => import('@/views/device-detail.vue'),
            meta: { title: '设备详情', hidden: true, keepAlive: true },
          },
        ],
      },

      // 通知管理
      {
        path: 'notification',
        redirect: '/notification/management',
        meta: { title: '通知管理', icon: 'Bell' },
        children: [
          {
            path: 'management',
            name: 'NotificationManagement',
            component: () => import('@/views/notification-management.vue'),
            meta: { title: '通知管理', keepAlive: true },
          },
          {
            path: 'detail/:id',
            name: 'NotificationDetail',
            component: () => import('@/views/notification-detail.vue'),
            meta: { title: '通知详情', hidden: true, keepAlive: true },
          },
        ],
      },

      // 搜索管理
      {
        path: 'search',
        redirect: '/search/log/management',
        meta: { title: '搜索管理', icon: 'Search' },
        children: [
          {
            path: 'log/management',
            name: 'SearchLogManagement',
            component: () => import('@/views/search-log-management.vue'),
            meta: { title: '搜索日志', keepAlive: true },
          },
          {
            path: 'trending/management',
            name: 'SearchTrendingManagement',
            component: () => import('@/views/search-trending-management.vue'),
            meta: { title: '搜索趋势', keepAlive: true },
          },
        ],
      },

      // 员工管理
      {
        path: 'staff',
        redirect: '/staff/management',
        meta: { title: '员工管理', icon: 'User' },
        children: [
          {
            path: 'management',
            name: 'StaffManagement',
            component: () => import('@/views/staff-management.vue'),
            meta: { title: '员工管理', keepAlive: true },
          },
          {
            path: 'detail/:id',
            name: 'StaffDetail',
            component: () => import('@/views/staff-detail.vue'),
            meta: { title: '员工详情', hidden: true, keepAlive: true },
          },
          {
            path: 'role/management',
            name: 'StaffRoleManagement',
            component: () => import('@/views/staff-roles.vue'),
            meta: { title: '角色权限', keepAlive: true },
          },
        ],
      },
      // 系统设置
      {
        path: 'system',
        redirect: '/system/config',
        meta: { title: '系统设置', icon: 'Setting' },
        children: [
          {
            path: 'config',
            name: 'SystemConfig',
            component: () => import('@/views/system/config.vue'),
            meta: { title: '系统配置' },
          },
          {
            path: 'logs',
            name: 'SystemLogs',
            component: () => import('@/views/system/logs.vue'),
            meta: { title: '操作日志' },
          },
          {
            path: 'app-version',
            name: 'AppVersionManagement',
            component: () => import('@/views/app-version-management.vue'),
            meta: { title: 'App版本管理', keepAlive: true },
          },
        ],
      },

      // 开发者工具
      {
        path: 'developer',
        redirect: '/developer/schemas',
        meta: { title: '开发者工具', icon: 'Monitor', hidden: false },
        children: [
          {
            path: 'schemas',
            name: 'DeveloperSchemas',
            component: () => import('@/views/developer/schemas.vue'),
            meta: { title: 'Schema 查看器' },
          },
          {
            path: 'schema-form',
            name: 'DeveloperSchemaForm',
            component: () => import('@/views/developer/schema-form.vue'),
            meta: { title: 'SchemaForm 示例' },
          },
          {
            path: 'schema-table',
            name: 'DeveloperSchemaTable',
            component: () => import('@/views/developer/schema-table.vue'),
            meta: { title: 'SchemaTable 示例' },
          },
          {
            path: 'api-list-table',
            name: 'DeveloperApiListTable',
            component: () => import('@/views/developer/api-list-table.vue'),
            meta: { title: 'ApiListTable 示例' },
          },
          {
            path: 'field-components',
            name: 'DeveloperFieldComponents',
            component: () => import('@/views/developer/field-components.vue'),
            meta: { title: '字段组件展示' },
          },
          {
            path: 'apis',
            name: 'DeveloperApis',
            component: () => import('@/views/developer/apis.vue'),
            meta: { title: 'API 查看器' },
          },
          {
            path: 'constants',
            name: 'DeveloperConstants',
            component: () => import('@/views/developer/constants.vue'),
            meta: { title: '常量查看器' },
          },
          {
            path: 'watermill',
            name: 'WatermillManagement',
            component: () => import('@/views/watermill-management.vue'),
            meta: { title: 'Watermill 消息队列' },
          },
          {
            path: 'stream-webhook',
            name: 'StreamWebhookManagement',
            component: () => import('@/views/stream-webhook-management.vue'),
            meta: { title: 'Stream Webhook 事件' },
          },
        ],
      },

      // 测试功能
      {
        path: 'test',
        redirect: '/test/index',
        meta: { title: '测试功能', icon: 'Tools', hidden: false },
        children: [
          {
            path: 'index',
            name: 'TestIndex',
            component: () => import('@/views/test/index.vue'),
            meta: { title: '测试主页' },
          },
          {
            path: 'api-form',
            name: 'ApiFormTest',
            component: () => import('@/views/test/api-form-test.vue'),
            meta: { title: 'ApiForm 测试' },
          },
          {
            path: 'api-caller',
            name: 'ApiDialogTest',
            component: () => import('@/views/test/api-caller-test.vue'),
            meta: { title: 'API Caller 测试' },
          },
        ],
      },

      // 个人资料
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/my-profile.vue'),
        meta: { title: '个人资料', hidden: true, keepAlive: false },
      },

      // 重定向页面（用于刷新）
      {
        path: 'redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/redirect/index.vue'),
        meta: { noTab: true, hidden: true },
      },
    ],
  },
  // 404页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '页面不存在' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// 路由守卫
router.beforeEach((to, from, next) => {
  // TODO: 添加认证逻辑
  const token = localStorage.getItem('access_token')

  if (to.path === '/login') {
    if (token) {
      next('/')
    } else {
      next()
    }
  } else {
    if (token) {
      next()
    } else {
      next('/login')
    }
  }
})

// 路由后置守卫
router.afterEach((to) => {
  // 设置页面标题
  const title = to.meta.title ? `${to.meta.title} - SplatChat Admin` : 'SplatChat Admin'
  document.title = title
})

export default router
